import { useCallback, useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { apiErrorText, apiUrl } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { useAuth } from "./auth";
import { importErrorText, importsApi, type ImportRun } from "./contacts-api";
import ImportWizard from "./importwizard";
import ImportResult from "./import-result";
import { goAnchor } from "./app-nav";
import { locale, t, tn } from "@/lib/i18n";

/* «Импорт контактов»: история импортов с сервера и мастер загрузки CSV.
   Загрузка идёт в фоне (worker); по окончании — уведомление, история обновляется по событию и опросом, пока есть незавершённые. */

const STATUS: Record<ImportRun["status"], string> = { queued: t("В очереди"), running: t("Загружается"), done: t("Завершено"), failed: t("Не выполнен") };

const day = (iso: string) => new Date(iso).toLocaleDateString(locale);

function ImportDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user } = useAuth();
  // импорт — владелец, администраторы и менеджеры (проверяет и сервер)
  const canImport = !!user && user.role !== "agent";
  const [runs, setRuns] = useState<ImportRun[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState<"list" | "wizard">("list");
  // открытый результат импорта (по клику на строку истории); данные берём из свежего списка
  const [openId, setOpenId] = useState<number | null>(null);
  const openRun = runs.find((r) => r.id === openId) ?? null;
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // прошлые статусы — чтобы сообщить об окончании импорта
  const seen = useRef<Map<number, ImportRun["status"]>>(new Map());

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const load = useCallback(async () => {
    try {
      const list = await importsApi.history();
      for (const r of list) {
        const was = seen.current.get(r.id);
        if (was && was !== r.status && r.status === "done")
          flash(t("Импорт «{file}» завершён: добавлено {added}, обновлено {updated}", { file: r.fileName, added: tn(r.created, "контакт", "контакта", "контактов"), updated: r.updated }));
        if (was && was !== r.status && r.status === "failed") flash(t("Импорт «{file}» не выполнен — подробности в уведомлении", { file: r.fileName }));
        seen.current.set(r.id, r.status);
      }
      setRuns(list);
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!canImport) return;
    void load();
    // готовый импорт присылает уведомление — перечитываем историю
    return onServerEvent("notification", () => void load());
  }, [canImport, load]);

  // запасной опрос, пока что-то в очереди или загружается
  const pending = runs.some((r) => r.status === "queued" || r.status === "running");
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [pending, load]);

  const onNav = (id: string) => {
    if (id === "import") return;
    if (id === "ai-assist") {
      goAnchor("#ai-assist");
      return;
    }
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» пока не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const done = (run: ImportRun) => {
    seen.current.set(run.id, run.status);
    setRuns((prev) => [run, ...prev.filter((r) => r.id !== run.id)]);
    setMode("list");
    flash(t("Файл «{file}» загружается в фоне — по окончании придёт уведомление", { file: run.fileName }));
  };

  const th = "py-2.5 pe-3 text-start font-normal align-middle last:pe-0";

  return (
    <Band id="contacts-import" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} {t("/ настройки данных → импорт контактов")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Импортов: {n}", { n: canImport ? runs.length : "—" })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "import";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Импорт контактов доступен владельцу, администраторам и менеджерам. Агенты работают только со своими контактами.")}
              </p>
            </div>
          </div>

          {/* ── содержимое окна ── */}
          <div className="min-w-0">
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-3.5 py-2 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "import" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            {mode === "wizard" && canImport ? (
              <ImportWizard onClose={() => setMode("list")} onDone={done} />
            ) : openRun && canImport ? (
              <ImportResult run={openRun} onBack={() => setOpenId(null)} />
            ) : (
              <div className="p-3.5 md:p-5">
                <div className="flex flex-wrap items-start gap-2.5">
                  <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                    <i className="ri-upload-cloud-2-line text-base leading-none" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold leading-snug">{t("Импорт контактов")}</h3>
                    <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{t("Просмотрите прошлые импорты или начните новый.")}</p>
                  </div>
                  {canImport ? (
                    <a
                      href={apiUrl("/imports/template")}
                      download
                      className="inline-flex h-8 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-background px-3 text-[12px] font-semibold text-foreground transition hover:bg-muted"
                    >
                      <i className="ri-file-download-line" aria-hidden />
                      {t("Скачать шаблон")}
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setMode("wizard")}
                    disabled={!canImport}
                    title={canImport ? undefined : t("Импорт доступен владельцу, администраторам и менеджерам")}
                    className="inline-flex h-8 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <i className="ri-add-line" aria-hidden />
                    {t("Импорт контактов")}
                  </button>
                </div>

                {!canImport ? (
                  <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-14 text-center">
                    <span className="mx-auto grid size-20 place-items-center rounded-full bg-foreground/[.06] text-muted-foreground" aria-hidden>
                      <i className="ri-lock-line text-3xl leading-none" />
                    </span>
                    <p className="mt-4 text-[14px] font-semibold">{t("Импорт недоступен для вашей роли")}</p>
                    <p className="mx-auto mt-1.5 max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                      {t("Загрузить контакты из файла могут владелец, администраторы и менеджеры — попросите их, если нужно.")}
                    </p>
                  </div>
                ) : !loaded ? (
                  <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-14 text-center text-[12px] text-muted-foreground">
                    <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden /> {t("Загружаем историю…")}
                  </div>
                ) : runs.length === 0 ? (
                  <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-14 text-center">
                    <span className="mx-auto grid size-20 place-items-center rounded-full bg-foreground/[.06] text-muted-foreground" aria-hidden>
                      <i className="ri-file-chart-line text-3xl leading-none" />
                    </span>
                    <p className="mt-4 text-[14px] font-semibold">{t("Импортируйте первый контакт")}</p>
                    <p className="mx-auto mt-1.5 max-w-sm text-[12px] leading-relaxed text-muted-foreground">
                      {t("Импорт контактов нужен для массовой загрузки нескольких контактов на платформу.")}
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-start">
                      <thead>
                        <tr className="border-b border-border">
                          <th scope="col" className={th}>
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Файл")}</span>
                          </th>
                          <th scope="col" className={th}>
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Контакты")}</span>
                          </th>
                          <th scope="col" className={cn(th, "hidden md:table-cell")}>
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Пропущено")}</span>
                          </th>
                          <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Автор")}</span>
                          </th>
                          <th scope="col" className={cn(th, "hidden sm:table-cell")}>
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Дата")}</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((r) => (
                          <tr
                            key={r.id}
                            onClick={() => setOpenId(r.id)}
                            title={t("Открыть результат импорта")}
                            className="cursor-pointer border-b border-border/60 transition last:border-0 hover:bg-muted/40"
                          >
                            <td className="py-2.5 pe-3">
                              <div className="flex items-center gap-2">
                                <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                                  <i className="ri-file-text-line text-[13px] leading-none" />
                                </span>
                                <span className="min-w-0">
                                  <span className="block max-w-[16rem] truncate text-[13px] font-semibold leading-snug">{r.fileName}</span>
                                  <span
                                    className={cn(
                                      "mt-0.5 inline-flex items-center gap-1 text-[10.5px]",
                                      r.status === "failed" ? "text-red-500" : r.status === "done" ? "text-primary" : "text-amber-600",
                                    )}
                                  >
                                    {r.status === "queued" || r.status === "running" ? (
                                      <i className="ri-loader-4-line animate-spin leading-none" aria-hidden />
                                    ) : null}
                                    {STATUS[r.status]}
                                  </span>
                                </span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap py-2.5 pe-3 text-[12px] tabular-nums">
                              +{r.created}
                              {r.updated > 0 ? <span className="text-muted-foreground"> / ~{r.updated}</span> : null}
                              <span className="ms-1 text-[11px] text-muted-foreground">{t("из {n}", { n: r.rows })}</span>
                            </td>
                            <td
                              className="hidden whitespace-nowrap py-2.5 pe-3 text-[12px] tabular-nums text-muted-foreground md:table-cell"
                              title={
                                r.errors.length > 0
                                  ? r.errors
                                      .slice(0, 5)
                                      .map((e) => t("Строка {line}: {text}", { line: e.line, text: `${importErrorText[e.error] ?? e.error}${e.value ? ` (${e.value})` : ""}` }))
                                      .join("\n")
                                  : undefined
                              }
                            >
                              {r.skipped}
                              {r.errors.length > 0 ? <i className="ri-information-line ms-1 text-[12px] leading-none text-amber-500" aria-hidden /> : null}
                            </td>
                            <td className="hidden whitespace-nowrap py-2.5 pe-3 text-[12px] text-muted-foreground sm:table-cell">{r.author || "—"}</td>
                            <td className="hidden whitespace-nowrap py-2.5 pe-3 text-[12px] tabular-nums text-muted-foreground sm:table-cell">
                              {day(r.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
                  <span>
                    {runs.length === 0
                      ? t("Пока пусто — контакты добавляются вручную или через каналы")
                      : t("{n} · последний {date}", { n: tn(runs.length, "импорт", "импорта", "импортов"), date: day(runs[0].createdAt) })}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-refresh-line text-[13px] leading-none text-primary" aria-hidden />
                    {t("Загрузка идёт в фоне — придёт уведомление")}
                  </span>
                </div>

                <p className="mt-3 text-[11px] text-muted-foreground">
                  {t("Нажмите на импорт, чтобы увидеть результат: сколько создано и обновлено, пропущенные строки с причиной и загруженные контакты. В истории — последние 50 импортов.")}
                </p>
              </div>
            )}

            {toast ? (
              <div
                role="status"
                className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
              >
                <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                {toast}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Band>
  );
}

export default ImportDemo;